import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Button,
  ColumnLayout,
  Flashbar,
  Form,
  FormField,
  Input,
  Header,
  Container,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3';

import { Redirect, useLocation } from 'react-router-dom';
import * as validate from '../../commons/validationUtils';
import { Page } from '../../routes/Paths';
import { editTemplate } from 'src/api/permissions';
import { FORM_CONTENT_TYPE } from 'src/commons/constants';
import {
  getAccessManagementComponent,
  getAccessRequestWorkflowComponent,
  getDataAgreementOptionsForOwner,
  isAccessManagementType,
  isAccessRequestWorkflowType,
  populateAccessManagementInfo,
  populateAccessRequestWorkflows,
} from 'src/components/templates/common';
import { isD3AServiceAvailable } from 'src/api/config';
import { AccessRequestWorkflow } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { getDataAgreement } from 'src/api/d3a';

export interface EditTemplateProps {
  setContentType: any;
  match: any;
  activeGroup: string;
  activeWorkspace?: any;
}

export const EditTemplate = (props: EditTemplateProps) => {
  const [redirect, setRedirect] = useState(undefined);
  const { state } = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [templateName, setTemplateName] = useState(null);
  const [description, setDescription] = useState(null);
  const [simTemplateName, setSimTemplateName] = useState(null);
  const [simTemplateLink, setSimTemplateLink] = useState(null);
  // common fields
  const [approvalTemplateLink, setApprovalTemplateLink] = useState(null);
  const [approvalTemplateTitle, setApprovalTemplateTitle] = useState(null);
  const [autoApproval, setAutoApproval] = useState(false);
  const [allowMergeApprovalWorkflow, setAllowMergeApprovalWorkflow] = useState(false);
  // access request workflow fields
  const [arwRuleName, setArwRuleName] = useState(null);
  const [arwRuleDesc, setArwRuleDesc] = useState(null);
  const [arwRule, setArwRule] = useState(null);
  const [arwDataAgreement, setArwDataAgreement] = useState(null);
  const [arwDataAgreementModalVisible, setArwDataAgreementModalVisible] = useState(false);
  const [arwDataAgreementId, setArwDataAgreementId] = useState(null);
  const [arwDataAgreementName, setArwDataAgreementName] = useState(null);
  const [arwDataAgreementOptions, setArwDataAgreementOptions] = useState([]);
  const [arwDataAgreementOwnerId, setArwDataAgreementOwnerId] = useState(undefined);

  useEffect(() => {
    props.setContentType(FORM_CONTENT_TYPE);
    if (!state) {
      setRedirect(Page.TEMPLATES);
    } else if (state.template) {
      // set current variable from state template.
      setTemplateName(state.template.name);
      setDescription(state.template.description);
      if (isAccessManagementType(state.template)) {
        if (state.template.accessManagementInfo?.simTemplates) {
          setSimTemplateLink(state.template.accessManagementInfo?.simTemplates[0].simTemplateLink);
          setSimTemplateName(state.template.accessManagementInfo?.simTemplates[0].simTemplateName);
        }
        if (state.template.accessManagementInfo?.approvalsWorkflowTemplate) {
          setApprovalTemplateLink(state.template.accessManagementInfo?.approvalsWorkflowTemplate.approvalTemplateLink);
          setApprovalTemplateTitle(
            state.template.accessManagementInfo?.approvalsWorkflowTemplate.approvalTemplateTitle,
          );
          setAutoApproval(state.template.accessManagementInfo?.approvalsWorkflowTemplate.autoApproval);
          setAllowMergeApprovalWorkflow(
            state.template.accessManagementInfo?.approvalsWorkflowTemplate.allowMergeApprovalWorkflow,
          );
        }
      }

      if (isAccessRequestWorkflowType(state.template) && state.template.accessRequestWorkflows) {
        const arw: AccessRequestWorkflow = state.template.accessRequestWorkflows[0];
        if (arw.conditionRule) {
          setArwRuleName(arw.conditionRule.name);
          setArwRuleDesc(arw.conditionRule.description);
          setArwRule(arw.conditionRule.rule);
        }
        if (arw.dataAgreement) {
          setArwDataAgreementName(arw.dataAgreement.agreementName);
          setArwDataAgreementId(arw.dataAgreement.agreementId);
          setArwDataAgreementOwnerId(arw.dataAgreement.agreementOwnerId);
          // fetch current agreement and set.
          if (isD3AServiceAvailable()) {
            getDataAgreement({ id: arw.dataAgreement.agreementId }).then(
              (apiResp) => {
                setArwDataAgreement(apiResp.dataAgreements[0]);
              },
              (err) => {
                console.error(`Failed to get data agreement with id: ${arw.dataAgreement.agreementId}`, err);
                setArwDataAgreement(null);
              },
            );
          }
        }
        if (arw.approvalsWorkflowTemplate) {
          setApprovalTemplateLink(arw.approvalsWorkflowTemplate.approvalTemplateLink);
          setApprovalTemplateTitle(arw.approvalsWorkflowTemplate.approvalTemplateTitle);
          setAutoApproval(arw.approvalsWorkflowTemplate.autoApproval);
          setAllowMergeApprovalWorkflow(arw.approvalsWorkflowTemplate.allowMergeApprovalWorkflow);
        }
      }
      if (isD3AServiceAvailable()) {
        // set agreement options
        const ownerIds: string[] = props.activeWorkspace
          ? [props.activeWorkspace.workspaceId, props.activeWorkspace.groupId]
          : [props.activeGroup];
        getDataAgreementOptionsForOwner(ownerIds).then((options) => setArwDataAgreementOptions(options));
      }
    }
  }, []);

  const shouldDisableForm = () => {
    // check if anything has changed, else form is disabled.
    if (
      state.template.name == templateName &&
      state.template.description == description &&
      ((isAccessManagementType(state.template) &&
        (state.template.accessManagementInfo?.simTemplates
          ? state.template.accessManagementInfo?.simTemplates?.[0]?.simTemplateName == simTemplateName &&
            state.template.accessManagementInfo?.simTemplates?.[0]?.simTemplateLink == simTemplateLink
          : true) &&
        (state.template.accessManagementInfo?.approvalsWorkflowTemplate
          ? state.template.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateLink ==
              approvalTemplateLink &&
            state.template.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateTitle ==
              approvalTemplateTitle &&
            state.template.accessManagementInfo?.approvalsWorkflowTemplate?.autoApproval == autoApproval &&
            state.template.accessManagementInfo?.approvalsWorkflowTemplate?.allowMergeApprovalWorkflow ==
              allowMergeApprovalWorkflow
          : true)) ||
        (isAccessRequestWorkflowType(state.template) &&
          (state.template.accessRequestWorkflows?.[0]?.conditionRule
            ? state.template.accessRequestWorkflows?.[0]?.conditionRule?.name == arwRuleName &&
              state.template.accessRequestWorkflows?.[0]?.conditionRule?.description == arwRuleDesc
            : true) &&
          (state.template.accessRequestWorkflows?.[0]?.dataAgreement
            ? state.template.accessRequestWorkflows?.[0]?.dataAgreement?.agreementId == arwDataAgreementId &&
              state.template.accessRequestWorkflows?.[0]?.dataAgreement?.agreementName == arwDataAgreementName
            : true) &&
          (state.template.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate
            ? state.template.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateLink ==
                approvalTemplateLink &&
              state.template.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateTitle ==
                approvalTemplateTitle &&
              state.template.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.autoApproval == autoApproval &&
              state.template.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.allowMergeApprovalWorkflow ==
                allowMergeApprovalWorkflow
            : true)))
    ) {
      return true;
    }
    // check if form entries are false, then form is disabled
    let isApprovalFalsy = validate.isFalsy(approvalTemplateTitle) || validate.isFalsy(approvalTemplateLink);
    let isDataAgreementFalsy = validate.isFalsy(arwDataAgreementId);
    let isSimFalsy = validate.isFalsy(simTemplateName) || validate.isFalsy(simTemplateLink);
    return (
      validate.isFalsy(templateName) ||
      validate.isFalsy(description) ||
      (isAccessManagementType(state.template) && isSimFalsy && isApprovalFalsy) ||
      (isAccessRequestWorkflowType(state.template) && isDataAgreementFalsy && isApprovalFalsy)
    );
  };

  const handleConfirm = async () => {
    setButtonLoading(true);
    let accessManagementInfo = undefined;
    let accessRequestWorkflows = undefined;
    if (isAccessManagementType(state?.template)) {
      accessManagementInfo = populateAccessManagementInfo(
        simTemplateName,
        simTemplateLink,
        approvalTemplateLink,
        approvalTemplateTitle,
        autoApproval,
        allowMergeApprovalWorkflow,
      );
    }
    if (isAccessRequestWorkflowType(state?.template)) {
      accessRequestWorkflows = populateAccessRequestWorkflows(
        arwRule,
        arwRuleName,
        arwRuleDesc,
        arwDataAgreementId,
        arwDataAgreementName,
        arwDataAgreementOwnerId,
        approvalTemplateLink,
        approvalTemplateTitle,
        autoApproval,
        allowMergeApprovalWorkflow,
      );
    }
    try {
      let editInput = {
        templateId: state.template?.templateId,
        resourceId: state?.template?.resourceId,
        name: templateName,
        description: description,
        accessManagementInfo: accessManagementInfo,
        accessRequestWorkflows: accessRequestWorkflows,
        templateTriggerRules: state?.template?.templateTriggerRules,
      };
      console.log('invoking edit withe body', editInput);
      const editResponse = await editTemplate(editInput);
      const status = editResponse.message == 'SUCCESS' ? 'success' : 'error';
      setButtonLoading(false);
      setRedirect({
        pathname: Page.TEMPLATES,
        state: {
          status,
          message: 'Template edited successfully',
        },
      });
    } catch (err) {
      setNotifications([
        {
          type: 'error',
          content: `Failed to Edit the template for template id: ${state.template?.templateId}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  return (
    <div>
      {redirect && <Redirect push to={redirect} />}
      <Flashbar items={notifications}></Flashbar>
      <Form
        header={
          <Header variant='h1' description='Please specify the details of the template.'>
            Edit Template
          </Header>
        }
        actions={
          <SpaceBetween direction='horizontal' size='s'>
            <Button variant='link' onClick={() => setRedirect(Page.TEMPLATES)}>
              Cancel
            </Button>
            <Button variant='primary' loading={buttonLoading} onClick={handleConfirm} disabled={shouldDisableForm()}>
              {'Edit template'}
            </Button>
          </SpaceBetween>
        }
      >
        <Container header={<Header variant='h2'>Template details</Header>}>
          <ColumnLayout>
            <SpaceBetween size='m'>
              <FormField label={<div>Template ID</div>}>
                <Input name='templateId' value={state?.template?.templateId} disabled={true} />
              </FormField>
              <FormField label={<div>Owner</div>}>
                <Input name='ownerId' value={state?.template?.ownerId} disabled={true} />
              </FormField>
              <FormField label={<div>Name</div>} description='Name of the template'>
                <Input
                  name='templateName'
                  placeholder='Name of the template'
                  ariaRequired={true}
                  onChange={(event) => setTemplateName(event.detail.value)}
                  invalid={templateName !== null && validate.isFalsy(templateName)}
                  value={templateName}
                />
              </FormField>
              <FormField label={<div>Description</div>} description='A brief description about template.'>
                <Textarea
                  name='description'
                  placeholder='A brief description about the template'
                  ariaRequired={true}
                  onChange={(event) => setDescription(event.detail.value)}
                  invalid={description !== null && validate.isFalsy(description)}
                  value={description}
                />
              </FormField>
              <FormField label={<div>Template type</div>}>
                <Input name='ownerId' value={state?.template?.templateType} disabled={true} />
              </FormField>
              {isAccessManagementType(state?.template) &&
                getAccessManagementComponent(
                  simTemplateName,
                  setSimTemplateName,
                  simTemplateLink,
                  setSimTemplateLink,
                  approvalTemplateTitle,
                  setApprovalTemplateTitle,
                  approvalTemplateLink,
                  setApprovalTemplateLink,
                  autoApproval,
                  setAutoApproval,
                  allowMergeApprovalWorkflow,
                  setAllowMergeApprovalWorkflow,
                )}

              {isAccessRequestWorkflowType(state?.template) &&
                getAccessRequestWorkflowComponent(
                  arwRuleName,
                  setArwRuleName,
                  arwRuleDesc,
                  setArwRuleDesc,
                  arwRule,
                  setArwRule,
                  arwDataAgreementOptions,
                  arwDataAgreement,
                  setArwDataAgreement,
                  arwDataAgreementModalVisible,
                  setArwDataAgreementModalVisible,
                  setArwDataAgreementOwnerId,
                  arwDataAgreementId,
                  setArwDataAgreementId,
                  arwDataAgreementName,
                  setArwDataAgreementName,
                  approvalTemplateTitle,
                  setApprovalTemplateTitle,
                  approvalTemplateLink,
                  setApprovalTemplateLink,
                  autoApproval,
                  setAutoApproval,
                  allowMergeApprovalWorkflow,
                  setAllowMergeApprovalWorkflow,
                )}
            </SpaceBetween>
          </ColumnLayout>
        </Container>
      </Form>
    </div>
  );
};
